/* KANIZ — Threads of Home. Dependency-free storefront behaviour.
   Everything degrades to native links and forms without JavaScript. */
(() => {
  'use strict';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOn = () => document.body.dataset.motion === 'on' && !reducedMotion.matches;
  const KZ = window.KANIZ || { routes: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* Dialogs ---------------------------------------------------------- */
  let lastTrigger = null;
  const openDialog = (dialog, trigger) => {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    lastTrigger = trigger || document.activeElement;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('dialog-open');
    $$(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'true'));
    const focusTarget = dialog.querySelector('[autofocus], input[type="search"]');
    if (focusTarget) focusTarget.focus();
    return true;
  };
  const closeDialog = dialog => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (openDialog(dialog, opener)) event.preventDefault();
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) closeDialog(closer.closest('dialog'));
  });
  const wireDialog = dialog => {
    if (dialog.dataset.wired) return;
    dialog.dataset.wired = 'true';
    dialog.addEventListener('click', event => { if (event.target === dialog) closeDialog(dialog); });
    dialog.addEventListener('close', () => {
      if (!$$('dialog[open]').length) document.documentElement.classList.remove('dialog-open');
      $$(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'false'));
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
    });
  };

  /* Header ----------------------------------------------------------- */
  const initHeader = () => {
    const header = $('[data-header]');
    if (!header) return;
    const navDetails = $$('[data-nav-details]', header);
    const update = () => {
      const open = navDetails.some(d => d.open);
      header.classList.toggle('is-solid', !header.hasAttribute('data-overlay') || window.scrollY > 40 || open || header.matches(':hover') && window.innerWidth >= 990);
    };
    if (header.hasAttribute('data-overlay')) {
      window.addEventListener('scroll', update, { passive: true });
      header.addEventListener('mouseenter', update);
      header.addEventListener('mouseleave', update);
      header.addEventListener('focusin', () => header.classList.add('is-solid'));
      header.addEventListener('focusout', () => setTimeout(update, 0));
    }
    navDetails.forEach(details => details.addEventListener('toggle', () => {
      if (details.open) navDetails.forEach(other => { if (other !== details) other.open = false; });
      update();
    }));
    document.addEventListener('click', event => {
      navDetails.forEach(d => { if (d.open && (!d.contains(event.target) || event.target.closest('a'))) d.open = false; });
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      navDetails.forEach(d => { if (d.open) { d.open = false; d.querySelector('summary').focus(); } });
    });
    update();
  };

  /* Scroll reveals --------------------------------------------------- */
  let revealObserver = null;
  const initReveals = scope => {
    const items = $$('[data-reveal], [data-reveal-image]', scope);
    if (!motionOn() || !('IntersectionObserver' in window)) { items.forEach(el => el.classList.add('is-revealed')); return; }
    revealObserver = revealObserver || new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); revealObserver.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    items.forEach(el => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) el.classList.add('is-revealed');
      else revealObserver.observe(el);
    });
  };

  /* Cinematic hero --------------------------------------------------- */
  const initHero = scope => $$('[data-hero]', scope).forEach(hero => {
    if (hero.dataset.ready) return;
    hero.dataset.ready = 'true';
    const frames = $$('[data-hero-frame]', hero);
    const ticks = $$('[data-hero-tick]', hero);
    const caption = $('[data-hero-caption]', hero);
    const video = $('[data-hero-video]', hero);
    const toggle = $('[data-hero-toggle]', hero);
    const duration = Number(hero.dataset.frameDuration || 4500);
    let index = 0, timer = null, playing = false;
    requestAnimationFrame(() => hero.classList.add('is-ready'));
    if (motionOn()) hero.setAttribute('data-motion-ok', '');

    const show = i => {
      frames.forEach((f, n) => f.classList.toggle('is-active', n === i));
      ticks.forEach((t, n) => { t.classList.toggle('is-active', n === i); t.classList.toggle('is-done', n < i); });
      if (caption) caption.textContent = frames[i]?.dataset.caption || '';
    };
    const advance = () => {
      if (index < frames.length - 1) { index += 1; show(index); timer = setTimeout(advance, duration); }
      else if (hero.dataset.loop === 'true') { index = 0; ticks.forEach(t => t.classList.remove('is-done')); show(0); timer = setTimeout(advance, duration); }
      else { playing = false; setToggle(); }
    };
    const setToggle = () => {
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(!playing));
      $('[data-label-pause]', toggle).hidden = !playing;
      $('[data-label-play]', toggle).hidden = playing;
    };

    // Video: choose one source for the viewport so only one file downloads.
    if (video) {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const src = (mobile && video.dataset.srcMobile) || video.dataset.srcDesktop || video.dataset.srcMobile;
      if (src && motionOn()) {
        video.src = src;
        video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
        video.play().then(() => { playing = true; setToggle(); }).catch(() => {});
      } else if (video.parentElement) {
        video.remove();
      }
    }
    const videoActive = () => video && document.contains(video) && video.src;

    if (!videoActive() && frames.length > 1 && motionOn()) {
      playing = true; show(0); timer = setTimeout(advance, duration);
    } else if (frames.length) {
      show(0);
    }
    setToggle();
    if (toggle) {
      if (!videoActive() && (frames.length < 2 || !motionOn())) toggle.hidden = true;
      toggle.addEventListener('click', () => {
        if (videoActive()) {
          if (video.paused) { video.play(); playing = true; } else { video.pause(); playing = false; }
        } else if (playing) {
          clearTimeout(timer); playing = false; hero.removeAttribute('data-motion-ok');
          ticks.forEach(t => t.style.animationPlayState = 'paused');
        } else {
          playing = true; if (motionOn()) hero.setAttribute('data-motion-ok', '');
          ticks.forEach(t => t.style.animationPlayState = '');
          if (index >= frames.length - 1) { index = -1; ticks.forEach(t => t.classList.remove('is-done')); }
          advance();
        }
        setToggle();
      });
    }
    document.addEventListener('visibilitychange', () => { if (document.hidden && videoActive()) video.pause(); else if (!document.hidden && videoActive() && playing) video.play().catch(() => {}); });
  });

  /* Product cards carousel buttons ---------------------------------- */
  const initCarousels = scope => $$('[data-carousel]', scope).forEach(root => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const track = $('[data-carousel-track]', root);
    const prev = $('[data-carousel-prev]', root);
    const next = $('[data-carousel-next]', root);
    if (!track || !prev || !next) return;
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 300) + 24;
    const sync = () => {
      prev.disabled = track.scrollLeft < 8;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
      root.querySelector('.carousel-controls')?.toggleAttribute('hidden', track.scrollWidth <= track.clientWidth + 8);
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: motionOn() ? 'smooth' : 'auto' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: motionOn() ? 'smooth' : 'auto' }));
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });

  /* Quantity steppers ------------------------------------------------ */
  document.addEventListener('click', event => {
    const btn = event.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.closest('.quantity')?.querySelector('input');
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = input.min !== '' ? Number(input.min) : 0;
    const max = input.max !== '' ? Number(input.max) : Infinity;
    const value = Math.min(max, Math.max(min, (Number(input.value) || 0) + step * Number(btn.dataset.qtyStep)));
    if (value !== Number(input.value)) { input.value = value; input.dispatchEvent(new Event('change', { bubbles: true })); }
  });

  /* Product page ----------------------------------------------------- */
  const money = el => el ? el.textContent : '';
  const initProduct = scope => $$('[data-product]', scope).forEach(root => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const json = $('[data-product-json]', root);
    const data = json ? JSON.parse(json.textContent) : null;
    const form = $('[data-product-form]', root);
    const idInput = form ? $('input[name="id"]', form) : null;
    const addButton = $('[data-add]', root);
    const priceEl = $('[data-price]', root);
    const compareEl = $('[data-compare]', root);
    const availEl = $('[data-availability]', root);
    const qty = $('[data-quantity]', root);
    const sticky = $('[data-sticky-buy]', root);
    let syncSticky = () => {};
    $$('[data-js-enable]', root).forEach(input => { input.disabled = false; });

    // Gallery
    const track = $('[data-gallery-track]', root);
    const slides = track ? $$('[data-gallery-slide]', track) : [];
    const counter = $('[data-gallery-current]', root);
    const dots = $$('[data-gallery-dot]', root);
    let current = 0;
    // While the gallery swipes sideways, its height follows the photograph in view, so a landscape or
    // square photograph never leaves an empty band beneath it.
    const fitTrack = () => {
      if (!track) return;
      const swiping = track.scrollWidth > track.clientWidth + 4;
      track.style.height = swiping && slides[current] ? `${slides[current].offsetHeight}px` : '';
    };
    const setCurrent = i => {
      if (i < 0) return;
      current = i;
      if (counter) counter.textContent = String(i + 1);
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      fitTrack();
    };
    if (track && slides.length > 1) {
      fitTrack();
      window.addEventListener('resize', fitTrack, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(fitTrack).observe(slides[0]);
    }
    if (track && slides.length > 1) {
      // The photograph in view is read from the scroll position (the track's height follows the current photograph,
      // so a taller neighbour is never fully visible inside it).
      let frame = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const i = Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
          if (i !== current) setCurrent(i);
        });
      }, { passive: true });
    }
    const goToSlide = (i, smooth = true) => {
      const slide = slides[i];
      if (!slide || !track) return;
      if (track.scrollWidth > track.clientWidth + 4) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth && motionOn() ? 'smooth' : 'auto' });
      else if (smooth) slide.scrollIntoView({ block: 'nearest', behavior: motionOn() ? 'smooth' : 'auto' });
    };
    dots.forEach((dot, i) => dot.addEventListener('click', () => goToSlide(i)));

    // Zoom
    const zoom = $('[data-zoom-dialog]', root);
    if (zoom) {
      wireDialog(zoom);
      const zoomImg = $('[data-zoom-img]', zoom);
      const zoomCount = $('[data-zoom-count]', zoom);
      const zoomables = $$('[data-zoom-src]', root);
      let zi = 0;
      const showZoom = i => {
        zi = (i + zoomables.length) % zoomables.length;
        const src = zoomables[zi];
        zoomImg.src = src.dataset.zoomSrc; zoomImg.alt = src.dataset.zoomAlt || '';
        if (zoomCount) zoomCount.textContent = `${zi + 1} / ${zoomables.length}`;
        const vp = zoomImg.parentElement;
        zoomImg.onload = () => { vp.scrollLeft = (vp.scrollWidth - vp.clientWidth) / 2; vp.scrollTop = (vp.scrollHeight - vp.clientHeight) / 2; };
      };
      zoomables.forEach((btn, i) => btn.addEventListener('click', () => { showZoom(i); openDialog(zoom, btn); }));
      $('[data-zoom-prev]', zoom)?.addEventListener('click', () => showZoom(zi - 1));
      $('[data-zoom-next]', zoom)?.addEventListener('click', () => showZoom(zi + 1));
      $('[data-zoom-viewport]', zoom)?.addEventListener('click', event => { if (event.target.tagName === 'IMG' || event.target.matches('[data-zoom-viewport]')) closeDialog(zoom); });
      zoom.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight') showZoom(zi + 1);
        if (event.key === 'ArrowLeft') showZoom(zi - 1);
      });
      if (zoomables.length < 2) $$('[data-zoom-prev], [data-zoom-next]', zoom).forEach(b => b.hidden = true);
    }

    // Variants
    const fieldsets = $$('[data-option-index]', root);
    const currentOptions = () => fieldsets.map(fs => { const c = $('input:checked', fs); return c ? c.value : null; });
    const updateAvailabilityMarks = selected => {
      if (!data) return;
      fieldsets.forEach((fs, idx) => $$('input', fs).forEach(input => {
        const trial = selected.slice(); trial[idx] = input.value;
        const match = data.variants.find(v => v.options.every((o, n) => o === trial[n]));
        input.toggleAttribute('data-unavailable', !match || !match.available);
      }));
    };
    const applyVariant = variant => {
      if (!variant) {
        if (addButton) { addButton.disabled = true; addButton.querySelector('[data-add-label]').textContent = KZ.strings.unavailable || 'Unavailable'; }
        if (availEl) { availEl.textContent = KZ.strings.unavailable || 'Unavailable'; availEl.dataset.state = 'sold-out'; }
        syncSticky();
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price;
      if (compareEl) { compareEl.hidden = !variant.compare; compareEl.textContent = variant.compare || ''; }
      if (addButton) { addButton.disabled = !variant.available; addButton.querySelector('[data-add-label]').textContent = variant.available ? KZ.strings.add : KZ.strings.soldOut; }
      if (availEl) { availEl.textContent = variant.available ? KZ.strings.available : KZ.strings.soldOut; availEl.dataset.state = variant.available ? 'available' : 'sold-out'; }
      $$('[data-option-selected]', root).forEach((el, i) => { el.textContent = variant.options[i] || ''; });
      if (qty) {
        const rule = variant.quantity_rule || {};
        qty.min = rule.min || 1; qty.step = rule.increment || 1;
        if (rule.max) qty.max = rule.max; else qty.removeAttribute('max');
        if (Number(qty.value) < qty.min) qty.value = qty.min;
      }
      if (sticky) { const sp = $('[data-sticky-price]', sticky); if (sp && priceEl) sp.textContent = money(priceEl); const sb = $('[data-sticky-add]', sticky); if (sb) { sb.disabled = !variant.available; sb.textContent = variant.available ? KZ.strings.add : KZ.strings.soldOut; } }
      syncSticky();
      if (variant.featured_media_id) {
        const i = slides.findIndex(s => s.dataset.mediaId === String(variant.featured_media_id));
        if (i > -1) goToSlide(i);
      }
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };
    fieldsets.forEach(fs => fs.addEventListener('change', () => {
      const selected = currentOptions();
      const variant = data?.variants.find(v => v.options.every((o, n) => o === selected[n]));
      updateAvailabilityMarks(selected);
      applyVariant(variant);
    }));
    if (fieldsets.length) updateAvailabilityMarks(currentOptions());

    // Add to bag
    if (form) form.addEventListener('submit', event => {
      if (document.body.dataset.cartType !== 'drawer' || !window.fetch) return;
      event.preventDefault();
      addToCart(form, addButton);
    });

    // Sticky buy bar (mobile): shown while the real Add to bag is off screen (still below the first screen, or
    // scrolled past), never for a piece that cannot be bought.
    if (sticky && addButton && 'IntersectionObserver' in window) {
      const stickyAdd = $('[data-sticky-add]', sticky);
      let offScreen = false;
      syncSticky = () => {
        const show = offScreen && !addButton.disabled;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (stickyAdd) stickyAdd.tabIndex = show ? 0 : -1;
      };
      const io = new IntersectionObserver(([entry]) => {
        const r = entry.boundingClientRect;
        offScreen = !entry.isIntersecting && (r.top < 0 || r.top >= window.innerHeight);
        syncSticky();
      }, { threshold: 0 });
      io.observe(addButton);
      stickyAdd?.addEventListener('click', () => { if (form.requestSubmit) form.requestSubmit(addButton); else addButton.click(); });
    }

    // Desktop information column: pinned by its top while it fits the window, by its bottom when it is taller
    const info = $('.product-info__sticky', root);
    if (info) {
      const desktop = window.matchMedia('(min-width: 990px)');
      const setTop = () => {
        if (!desktop.matches) { info.style.top = ''; return; }
        const headerVar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 84;
        const header = document.querySelector('.header-wrapper') || document.querySelector('.site-header');
        const headerH = Math.max(headerVar, header ? header.offsetHeight : 0);
        let top = Math.min(headerH + 32, window.innerHeight - info.offsetHeight - 32);
        if (top < headerH) {
          // Taller than the window: the top of the column rests under the header. Never leave a block cut through
          // there, or pressed against the header's edge.
          const edge = headerH - top;
          const pad = 12;
          const base = info.getBoundingClientRect().top;
          const blocks = [...info.children].flatMap(el => (el.matches('.accordions, .product-info__intro') && el.children.length ? [...el.children] : [el]));
          const cut = blocks.map(el => { const r = el.getBoundingClientRect(); return { a: r.top - base, b: r.bottom - base }; }).find(r => r.b > r.a && r.a < edge + pad && r.b > edge + 1);
          if (cut && edge + pad - cut.a <= 28) top += edge + pad - cut.a; // show the block whole; the column ends a little lower
          else if (cut && cut.b - edge <= window.innerHeight / 4) top -= cut.b - edge; // tuck the block under the header
        }
        info.style.top = `${Math.round(top)}px`;
      };
      if ('ResizeObserver' in window) new ResizeObserver(setTop).observe(info);
      window.addEventListener('resize', setTop, { passive: true });
      if (desktop.addEventListener) desktop.addEventListener('change', setTop);
      setTop();
    }
  });

  /* Cart ------------------------------------------------------------- */
  const cartDrawer = () => document.getElementById('CartDrawer');
  const updateCount = count => $$('[data-cart-count]').forEach(el => { el.textContent = count; el.hidden = !count; });
  const renderCartSections = sections => {
    if (!sections) return;
    const html = sections['cart-drawer'];
    const drawer = cartDrawer();
    if (html && drawer) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const count = doc.querySelector('[data-cart-drawer-count]');
      if (count) updateCount(Number(count.dataset.cartDrawerCount));
      const fresh = doc.querySelector('[data-cart-drawer-inner]');
      const current = $('[data-cart-drawer-inner]', drawer);
      if (fresh && current) current.replaceWith(document.importNode(fresh, true));
    }
  };
  const announce = message => {
    let live = document.getElementById('KZLive');
    if (!live) { live = document.createElement('div'); live.id = 'KZLive'; live.className = 'visually-hidden'; live.setAttribute('aria-live', 'polite'); document.body.appendChild(live); }
    live.textContent = ''; setTimeout(() => { live.textContent = message; }, 50);
  };
  const addToCart = async (form, button) => {
    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    button?.classList.add('is-loading'); if (button) button.disabled = true;
    const errorBox = form.querySelector('[data-form-error]');
    if (errorBox) errorBox.hidden = true;
    try {
      const response = await fetch(`${KZ.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || KZ.strings.error);
      renderCartSections(result.sections);
      announce(KZ.strings.added);
      openDialog(cartDrawer(), button);
    } catch (error) {
      if (errorBox) { errorBox.textContent = error.message || KZ.strings.error; errorBox.hidden = false; }
    } finally {
      button?.classList.remove('is-loading'); if (button) button.disabled = false;
    }
  };
  const changeLine = async (line, quantity, lineEl) => {
    lineEl?.classList.add('is-updating');
    try {
      const response = await fetch(`${KZ.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || KZ.strings.error);
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      renderCartSections(result.sections);
      updateCount(result.item_count);
    } catch (error) {
      lineEl?.classList.remove('is-updating');
      const err = lineEl?.querySelector('[data-line-error]'); if (err) { err.textContent = error.message; err.hidden = false; }
    }
  };
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-open-cart]');
    if (opener && document.body.dataset.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
      if (openDialog(cartDrawer(), opener)) event.preventDefault();
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove && remove.closest('[data-cart-drawer-inner]')) {
      event.preventDefault();
      changeLine(Number(remove.dataset.cartRemove), 0, remove.closest('[data-cart-line]'));
    }
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-qty]');
    if (input && input.closest('[data-cart-drawer-inner]')) changeLine(Number(input.dataset.cartQty), Number(input.value), input.closest('[data-cart-line]'));
    const note = event.target.closest('[data-cart-note]');
    if (note && window.fetch) fetch(`${KZ.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  });

  /* Collection filters ------------------------------------------------- */
  const initCollection = scope => $$('[data-collection]', scope).forEach(root => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const sort = $('[data-sort]', root);
    sort?.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value); url.searchParams.delete('page');
      window.location.href = url.toString();
    });
    $$('[data-apply-sort]', root).forEach(b => b.hidden = true);
  });

  /* Recommendations -------------------------------------------------- */
  const initRecommendations = scope => $$('[data-recommendations]', scope).forEach(async root => {
    if (root.dataset.ready || !window.fetch) return;
    root.dataset.ready = 'true';
    try {
      const response = await fetch(root.dataset.url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.querySelector('.product-card')) {
        root.innerHTML = fresh.innerHTML;
        root.closest('[data-recommendations-wrap]')?.removeAttribute('hidden');
        const carousel = root.closest('[data-carousel]'); if (carousel) delete carousel.dataset.ready;
        initReveals(root); initCarousels(root.closest('section') || document);
      } else {
        root.closest('[data-recommendations-wrap]')?.setAttribute('hidden', '');
      }
    } catch (e) { root.closest('[data-recommendations-wrap]')?.setAttribute('hidden', ''); }
  });

  /* Feature videos (muted ambient loops) ------------------------------- */
  const initAmbientVideos = scope => $$('[data-ambient-video]', scope).forEach(video => {
    if (video.dataset.ready) return;
    video.dataset.ready = 'true';
    if (!motionOn()) return;
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => { if (entry.isIntersecting) video.play().catch(() => {}); else video.pause(); }, { threshold: 0.25 }).observe(video);
  });

  /* Editorial hero film: one file per device, muted loop, a Pause control; reduced motion keeps the still -- */
  const initHeroFilms = scope => $$('[data-ed-film]', scope).forEach(film => {
    if (film.dataset.ready) return;
    film.dataset.ready = 'true';
    const video = $('[data-ed-film-video]', film);
    const toggle = $('[data-ed-film-toggle]', film);
    if (!video) return;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const src = (mobile && film.dataset.srcMobile) || film.dataset.srcDesktop || film.dataset.srcMobile;
    if (!src || !motionOn() || navigator.connection?.saveData) { video.remove(); return; }
    let userPaused = false;
    const play = () => video.play().catch(() => setToggle());
    const setToggle = () => {
      if (!toggle) return;
      const playing = !video.paused;
      $('[data-label-pause]', toggle).hidden = !playing;
      $('[data-label-play]', toggle).hidden = playing;
    };
    video.addEventListener('playing', () => { video.classList.add('is-playing'); setToggle(); });
    video.addEventListener('pause', setToggle);
    // A missing file or a browser without H.264: keep the still and drop the control.
    video.addEventListener('error', () => { userPaused = true; video.remove(); if (toggle) toggle.hidden = true; });
    video.src = src;
    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        if (video.paused) { userPaused = false; play(); } else { userPaused = true; video.pause(); }
      });
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { if (!userPaused) play(); } else video.pause(); }, { threshold: 0.2 }).observe(film);
    } else {
      play();
    }
  });

  /* Share a piece: native share sheet, otherwise copy the link ------------ */
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-share]');
    if (!button) return;
    const data = { title: button.dataset.shareTitle, url: button.dataset.shareUrl };
    if (navigator.share) { try { await navigator.share(data); } catch (e) { /* dismissed */ } return; }
    try {
      await navigator.clipboard.writeText(data.url);
      const label = button.textContent;
      button.textContent = button.dataset.copiedLabel;
      setTimeout(() => { button.textContent = label; }, 2400);
    } catch (e) { window.location.href = data.url; }
  });

  /* Product enquiry: prefill the contact message from ?enquiry=<piece> -------- */
  const initEnquiry = () => {
    const piece = new URLSearchParams(window.location.search).get('enquiry');
    const body = document.getElementById('ContactBody');
    if (!piece || !body || body.value) return;
    body.value = (KZ.strings.enquiryPrefix || 'Enquiry about:') + ' ' + piece.slice(0, 120) + '\n\n';
  };

  /* Cookie preferences: the footer link reopens Shopify's cookie banner. The banner only loads where consent is
     asked (visitors in the EU), so the link stays hidden until it is there. */
  const initCookiePreferences = () => {
    const links = $$('[data-cookie-preferences]');
    if (!links.length) return;
    const banner = () => (typeof window.privacyBanner?.showPreferences === 'function' ? window.privacyBanner : null);
    links.forEach(link => link.addEventListener('click', () => banner()?.showPreferences()));
    let tries = 0;
    const reveal = () => {
      if (banner()) links.forEach(link => { link.hidden = false; });
      else if (++tries < 20) setTimeout(reveal, 500);
    };
    reveal();
  };

  /* Boot ------------------------------------------------------------- */
  const init = (scope = document) => {
    $$('dialog[data-dialog], dialog.drawer', scope).forEach(wireDialog);
    initHero(scope); initReveals(scope); initCarousels(scope); initProduct(scope);
    initCollection(scope); initRecommendations(scope); initAmbientVideos(scope); initHeroFilms(scope);
  };
  const boot = () => { initHeader(); init(); initEnquiry(); initCookiePreferences(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:select', event => { $$('[data-reveal], [data-reveal-image]', event.target).forEach(el => el.classList.add('is-revealed')); });
})();
